import { Component, ContentChild, Directive, Input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { resolveAstralIconClass } from '../astral-icon-class.util';

export type AstralButtonSize = 'medium' | 'small' | 'extra-small';

@Directive({
  selector: '[astralButtonIcon]',
  standalone: true,
})
export class AstralButtonIconDirective {}

@Component({
  selector: 'astral-button',
  standalone: true,
  imports: [TranslatePipe],
  template: `
    <button
      class="astral-button"
      [class.astral-button--primary]="variant === 'primary'"
      [class.astral-button--secondary]="variant === 'secondary'"
      [class.astral-button--link-button]="variant === 'link-button'"
      [class.astral-button--small]="size === 'small'"
      [class.astral-button--extra-small]="size === 'extra-small'"
      [class.astral-button--icon-only]="isIconOnly"
      [attr.type]="type"
      [disabled]="disabled"
        [attr.aria-label]="ariaLabel ? (ariaLabel | translate) : null"
      [attr.aria-pressed]="ariaPressed"
        [attr.title]="title ? (title | translate) : null"
    >
      @if (hasProjectedIcon || resolvedIconClass) {
      <span class="astral-button__icon" aria-hidden="true">
        <ng-content select="[astralButtonIcon]"></ng-content>
        @if (resolvedIconClass) { <i [class]="resolvedIconClass"></i> }
      </span>
      }

      @if (showLinkVariantIcon) {
      <span class="astral-button__icon" aria-hidden="true">
        <i [class]="linkVariantIconClass"></i>
      </span>
      }

      @if (resolvedLabel) {
      <span class="astral-button__label">
        {{ resolvedLabel | translate }}
      </span>
      } @else {
        <span class="astral-button__label">
          <ng-content></ng-content>
        </span>
      }
    </button>
  `,
  styleUrls: ['./astral-button.component.scss'],
})
export class AstralButtonComponent {
  @Input() ariaLabel: string;
  @Input() ariaPressed: boolean | null = null;
  @Input() disabled = false;
  @Input() icon: string;
  @Input() label: string;
  @Input() labelKey: string;
  @Input() size: AstralButtonSize = 'medium';
  @Input() title: string;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() variant: 'primary' | 'secondary' | 'link-button' = 'primary';

  @ContentChild(AstralButtonIconDirective) buttonIcon: AstralButtonIconDirective;

  get hasProjectedIcon(): boolean {
    return !!this.buttonIcon;
  }

  get resolvedIconClass(): string | null {
    return resolveAstralIconClass(this.icon);
  }

  get linkVariantIconClass(): string | null {
    return resolveAstralIconClass('externalLink');
  }

  get showLinkVariantIcon(): boolean {
    return this.variant === 'link-button' && !this.hasProjectedIcon && !this.resolvedIconClass;
  }

  get resolvedLabel(): string {
    return this.labelKey ?? this.label ?? '';
  }

  get isIconOnly(): boolean {
    return !!this.resolvedIconClass && !this.resolvedLabel;
  }
}
